import { ArchiveData } from "@/features/archive";
import { ArchiveUsersResponse } from "@/features/archive/model/archive/ArchiveUsersResponse";
import {
	ArchiveProject,
	ArchivedProjectsRequest,
	ArchivedProjectsResponse,
} from "@/features/archive/model/archive/ArchivedProjectsRequest";
import { CompetenceMatrix } from "@/features/competency/model/competence/CompetenceMatrix";
import { CompetenceUserResult } from "@/features/competency/model/competence/CompetenceUserResult";
import {
	ActivateCompetencyTestEmailRequest,
	ActivateCompetencyTestEmailResponse,
	ActiveCompetencyEmailJobConfiguration,
	CreateCompetencyEmailJobRequest,
	CreateCompetencyEmailJobResponse,
} from "@/features/competency/model/competence/CreateCompetencyEmailJobRequest";
import { CreateCompetencyRequest } from "@/features/competency/model/competence/CreateCompetencyRequest";
import { ProcedureUpdateNoticeRequest } from "@/features/competency/model/competence/ProcedureUpdateNoticeRequest";
import {
	CompanyCompetenceGroupResponse,
	UpdateUserCompetenceDocumentStatusRequest,
	UpdateUserCompetenceDocumentStatusResponse,
	UserCompetence,
	UserCompetenceDocumentStatusRequest,
	UserCompetenceDocumentStatusResponse,
	UserCompetenceRequest,
} from "@/features/competency/model/competence/UserCompetence";
import { UserProcedureKnowledge } from "@/features/competency/model/competence/UserProcedureKnowledge";
import {
	ResourceDeleteRequest,
	ResourceDeleteResponse,
	ResourceUpdateRequest,
} from "@/features/competency/model/competency/Competency";
import {
	Group,
	GroupRequestType,
	GroupResource,
	GroupResourceRequest,
	GroupResourceType,
	GroupResources,
} from "@/features/groups/model/group/Group";
import { EditableLiveTag } from "@/features/live/model/satos-live/EditableLiveTag";
import { LiveTag } from "@/features/live/model/satos-live/LiveTag";
import { LiveTagValue } from "@/features/live/model/satos-live/LiveTagValue";
import { UpdateLiveTagRequest } from "@/features/live/model/satos-live/UpdateLiveTagRequest";
import {
	SavedLiveTagRequest,
	SavedLiveTagResponse,
} from "@/features/live/model/satos-live/settings/SavedTagValue";
import { LiveSettingsRequest } from "@/features/live/model/satos-live/settings/SettingsRequest";
import { LiveSettingsResponse } from "@/features/live/model/satos-live/settings/SettingsResponse";
import { CreateMaterialHandlingProcedure } from "@/features/material-handling/model/material-handling/CreateMaterialHandlingProcedure";
import { DeleteMaterialHandling } from "@/features/material-handling/model/material-handling/DeleteMaterialHandling";
import {
	MaterialHandlingApproveVersionRequest,
	MaterialHandlingApproveVersionResponse,
} from "@/features/material-handling/model/material-handling/MaterialHandlingApproveVersionRequest";
import { MaterialHandlingArchiveListItem } from "@/features/material-handling/model/material-handling/MaterialHandlingArchiveListItem";
import { MaterialHandlingEquipment } from "@/features/material-handling/model/material-handling/MaterialHandlingEquipment";
import { MaterialHandlingFastening } from "@/features/material-handling/model/material-handling/MaterialHandlingFastening";
import { MaterialHandlingList } from "@/features/material-handling/model/material-handling/MaterialHandlingList";
import {
	MaterialHandlingProcedure,
	UpdateMaterialHandlingProcedureRequest,
} from "@/features/material-handling/model/material-handling/MaterialHandlingProcedure";
import { MaterialHandlingReview } from "@/features/material-handling/model/material-handling/MaterialHandlingReview";
import { MaterialHandlingSearchResponse } from "@/features/material-handling/model/material-handling/MaterialHandlingSearchResponse";
import { MaterialHandlingSettings } from "@/features/material-handling/model/material-handling/MaterialHandlingSettings";
import { MaterialHandlingUnit } from "@/features/material-handling/model/material-handling/MaterialHandlingUnit";
import { NewMaterialHandlingProcedureReturn } from "@/features/material-handling/model/material-handling/NewMaterialHandlingProcedureReturn";
import { ReferenceUrl } from "@/features/material-handling/model/material-handling/ReferenceUrl";
import {
	CreateManualMeasurementGroupRequest,
	ManualMeasurementPointGroup,
	UpdateManualMeasurementGroupRequest,
} from "@/features/measurements";
import {
	ManualMeasurementPointRequest,
	ManualMeasurementPointResponse,
} from "@/features/measurements";
import { Checklist } from "@/features/procedures/model/checklist/Checklist";
import { ChecklistStepLog } from "@/features/procedures/model/checklist/ChecklistStep";
import {
	DeleteEdgeRequest,
	DeleteNodeRequest,
	DiagramNodeRequest,
	DiagramNodeResponse,
	NodeDiagramPublicityResponse,
	TogglePublishNodeDiagramRequest,
	UpdateEdgeRequest,
	UpdateGroupRequest,
	UpdateNodeParent,
	UpdateNodePosition,
} from "@/features/procedures/model/node-diagram/types";
import { DeleteProcedureRequest } from "@/features/procedures/model/procedure/DeleteProcedureRequest";
import { type CreateImportedChecklistRequest, DocumentImportPlan } from "@/features/procedures/model/procedure/DocumentImportPlan";
import { DocumentUpdateRequest } from "@/features/procedures/model/procedure/DocumentUpdateRequest";
import { EditProcedure } from "@/features/procedures/model/procedure/EditProcedure";
import { NodeVersionComparison } from "@/features/procedures/model/procedure/NodeVersionComparison";
import { ProcedureDetails } from "@/features/procedures/model/procedure/ProcedureDetail";
import { ProcedureGroup } from "@/features/procedures/model/procedure/ProcedureGroup";
import {
	CreateNodeRequest,
	DeleteProcedureNodeRequest,
	DeleteProcedureNodeResponse,
	UploadProcedureNodeDocumentRequest,
	UploadProcedureNodeDocumentResponse,
} from "@/features/procedures/model/procedure/ProcedureNodeDependency";
import { ProcedureDetailNode } from "@/features/procedures/model/types/node";
import {
	ProcedureNodeStepComment,
	SuggestionRequest,
	SuggestionResponse,
} from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import {
	DeleteProcedureChecklistRequest,
	DeleteProcedureChecklistResponse,
	QueryUpsertProcedureNodeSteps,
	StepStatus,
} from "@/features/procedures/model/procedure/ProcedureNodeSteps";
import {
	SaveProcedureRequest,
	SaveProcedureResponse,
} from "@/features/procedures/model/procedure/ProcedureSaveRequest";
import {
	DocumentForProcedureImport,
	DocumentFromProcedure,
	DocumentUsedForNodeResponse,
} from "@/features/procedures/model/procedure/ProcedureUseDocument";
import {
	ImportProcedureNodeDocumentRequest,
	RefreshSharePointDocumentRequest,
	RefreshSharePointDocumentResponse,
	UpdateProcedureNodeDocumentResponse,
	UpdateProcedureNodeRequest,
	UpdateProcedureNodeResponse,
} from "@/features/procedures/model/procedure/UpdateProcedureNodeRequest";
import { UpdateProcedureVersionRequest } from "@/features/procedures/model/procedure/UpdateProcedureVersionRequest";
import { UpdateStepStatusRequest } from "@/features/procedures/model/procedure/UpdateStepStatusRequest";
import { UpdateStepStatusResponse } from "@/features/procedures/model/procedure/UpdateStepStatusResponse";
import { UppdatedNodeResponse } from "@/features/procedures/model/procedure/UppdatedNodeResponse";
import { UppdatedProcedureVersion } from "@/features/procedures/model/procedure/UppdatedProcedureVersion";
import { CreateNewProcedureRequest } from "@/features/procedures/model/types/createNewProcedure";
import {
	AddLiveTagToStepRequest,
	CreateChecklistRequest,
	CreateChecklistResponse,
	CreateChecklistStepRequest,
	DeleteChecklistStepRequest,
	RemoveLiveTagFromStepRequest,
	RemoveManualMeasurementPointFromStepRequest,
	UpdateChecklistStepRequest,
} from "@/features/procedures/model/types/manualChecklist";
import { ActiveProcedure } from "@/features/procedures/model/types/procedure";
import { Procedure } from "@/features/procedures/model/types/procedure";
import { ProcedureAccordionMove } from "@/features/procedures/model/types/procedureAccordionMove";
import { ProcedureMetaData } from "@/features/procedures/model/types/procedureMetaData";
import { ProcedureUpdateSuggestionsResponse } from "@/features/procedures/model/types/procedureUpdateSuggestions";
import {
	CreateShiftEventCommentRequest,
	GroupItem,
	ShiftEventCrew,
	ShiftEventRequest,
	ShiftEventResponse,
	ShiftEventsPagedResponse,
	ShiftEventsQuery,
	ShiftOptions,
	UpdateShiftEventStatusRequest,
} from "@/features/shift/model/Shift/Shift";
import { ShiftConfigurationResponse } from "@/features/shift/model/Shift/ShiftConfiguration";
import { ShiftSummary } from "@/features/shift/model/Shift/ShiftSummary";
import {
	SortDirection,
	TaskAddCommentRequest,
	TaskCommentResponse,
	TaskGroup,
	TaskResponse,
	TaskSave,
} from "@/features/task/model/Task/TaskRequest";
import {
	StudentFailedReason,
	StudentFailedReasonRequest,
} from "@/features/training/model/Training/StudentFailedReason";
import {
	TrainingCourse,
	TrainingCourseRequest,
} from "@/features/training/model/Training/TrainingCourse";
import {
	TrainingDrill,
	TrainingDrillRequest,
} from "@/features/training/model/Training/TrainingDrill";
import {
	DrillSummary,
	TrainingDrillFolder,
	TrainingDrillFolderRequest,
} from "@/features/training/model/Training/TrainingDrillFolder";
import {
	CreateTrainingDrillSessionRequest,
	TrainingDrillSession,
	TrainingDrillSessionSummary,
	TrainingDrillSessionSummaryWithKpiValue,
	UpdateTrainingDrillSessionRequest,
} from "@/features/training/model/Training/TrainingDrillSession";
import {
	TrainingFile,
	TrainingFileRequest,
} from "@/features/training/model/Training/TrainingFile";
import {
	TrainingCourseSummary,
	TrainingFolder,
	TrainingFolderRequest,
} from "@/features/training/model/Training/TrainingFolder";
import {
	TrainingKpi,
	TrainingKpiRequest,
	TrainingKpiStatisticsQueryOptions,
} from "@/features/training/model/Training/TrainingKpi";
import { TrainingKpiStatistic } from "@/features/training/model/Training/TrainingKpiStatistic";
import {
	TrainingLink,
	TrainingLinkRequest,
} from "@/features/training/model/Training/TrainingLink";
import {
	CompleteTrainingSessionRequest,
	TrainingSession,
	TrainingSessionRequest,
	TrainingSessionSummary,
	TrainingSessionsQueryOptions,
} from "@/features/training/model/Training/TrainingSession";
import { DataAdapter } from "@/shared/api";
import { Edge } from "@xyflow/react";

import { GroupList } from "@/model/Application/GroupList";
import {
	CreateNewLanguageSettingsRequestType,
	LanguageSettingsRequestType,
	LanguageSettingsType,
} from "@/model/Application/LanguageSettings";
import { SearchResponse } from "@/model/Application/SearchResult";
import {
	BrandingSettingsType,
	GeneralFormData,
} from "@/model/Application/Settings/FormModels";
import {
	LabParameter,
	LabParameterRequest,
	LabSettings,
} from "@/model/Application/Settings/LabSettings";
import {
	BrandingSettingsRequest,
	CreateSystemSettingsRequest,
	OrganizationSettingsRequest,
	ProceduresSettingsRequestType,
	ServerEmailSettingsRequest,
	ServerPdfConversionSettingsRequest,
	ServerWordConvertorSettingsRequest,
	ShiftSettingsRequest,
	StartPageImageRequest,
	SystemSettingsResponse,
	TaskSettingsRequest,
} from "@/model/Application/Settings/RequestTypes";
import {
	ActiveDirectorySettings,
	AzureAdGroupSyncLogEntry,
	MeasurementsDaily,
	ProceduresSettings,
	ServerEmailSettingsResponse,
	ServerPdfConversionSettingsResponse,
	ServerWordConvertorSettingsResponse,
	ShiftName,
	ShiftNameRequest,
	ShiftSettingsResponse,
	ShiftSystem,
	ShiftSystemRequest,
	StartPageImageResponse,
	SystemInformation,
	TaskSettingsResponse,
} from "@/model/Application/Settings/SettingsResponseTypes";
import { Timezones } from "@/model/Application/Settings/Timezones";
import { ApplicationConfiguration } from "@/model/ApplicationConfiguration";
import { Company } from "@/model/Company/Company";
import { CreateCompanyRequest } from "@/model/Company/CreateCompanyRequest";
import {
	OrganizationModules,
	OrganizationModulesRequest,
} from "@/model/Modules/OrganizationModules";
import { PagedResponse } from "@/model/PagedResponse";
import {
	FileUpload,
	ResourceRemoteLocationRequest,
	ResourceRequestBase,
	ResourceResponse,
	ResourceUploadFileRequest,
} from "@/model/Resource/ResourceRequest";
import { JobStatusRequest, JobStatusResponse } from "@/model/Status/JobStatus";
import { Tag, UpdateTagsRequest } from "@/model/Tag/Tag";
import { GetTranslationsRequest } from "@/model/Translation/GetTranslationsRequest";
import {
	GetUserGroupMembershipStatusResponse,
	GroupMembershipType,
} from "@/model/User/Employee";
import {
	ChangeUserOrganizationRequest,
	ChangeUserOrganizationResponse,
	User,
	UserCompaniesResponse,
	UserSummaryResponse,
} from "@/model/User/User";
import { UserSummary } from "@/model/User/UserSummary";
import { UserCompanyConfiguration } from "@/model/user-company-configuration/UserCompanyConfiguration";

import { TrainingUsersResponse } from "@/service/TrainingQueries";
import { UpdateImportedChecklistRequest } from "@/features/procedures/model/types/checklist";

type NameColorPayload = {
	Name?: string;
	name?: string;
	Color?: string;
	color?: string;
};

type IdRequest = { id: number };
type GroupSaveClearRequest = { groupId: number };
type ELearningCourseRequestPayload = {
	name: string;
	description: string;
	passingScore: number;
};
type ELearningCourseUpdateRequestPayload = ELearningCourseRequestPayload & {
	id: number | string;
};
type ELearningQuestionRequestPayload = {
	question: string;
	correctAnswer: number;
	answerOptions: string[];
};
type AddELearningQuestionRequestPayload = ELearningQuestionRequestPayload & {
	courseId: number | string;
};
type ExamAnswerPayload = {
	questionId: number;
	answer: number;
};
type SubmitExamRequestPayload = {
	courseId: number;
	answers: ExamAnswerPayload[];
};
type PutExamRequestPayload = SubmitExamRequestPayload & {
	Id: number | string;
};
type UpdateExamQuestionPayload = ELearningQuestionRequestPayload & {
	id: number | string;
};
type MeasurementCommentRequestPayload = {
	LabMeasurementId: number | string;
	Comment: string;
	FileName?: string;
	File?: File | null;
};
type MeasurementCommentResponsePayload = {
	CommentId: number;
	Comment: string;
	Date: string;
	UserId: number;
	UserName: string;
};

export class SATOSPortal {
	static GetShiftGroups(): Promise<GroupItem[]> {
		return DataAdapter.Get<GroupItem[]>({
			endpoint: `Group/Shift`,
		});
	}

	static GetShiftNames(): Promise<ShiftName[]> {
		return DataAdapter.Get<ShiftName[]>({
			endpoint: `ShiftNames`,
		});
	}

	static GetShiftSummary(groupId: number) {
		return DataAdapter.Get<ShiftSummary>({
			endpoint: `Group/${groupId}/ShiftSummary`,
		});
	}

	static GetTaskSingle(groupId: number) {
		return DataAdapter.Get<unknown>({
			endpoint: `Tasks/${groupId}`,
		});
	}

	public static UpdateMeasurement(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(`LabModule/updatemeasurement`, request);
	}

	static GetCoursesEducation(): Promise<unknown[]> {
		return DataAdapter.Get<unknown[]>({
			endpoint: `ELearningCourses`,
		});
	}

	static GetCourseEducation(id: number): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `ELearningCourses/${id}`,
		});
	}

	static GetCourseEducationMedia(id: number): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `ELearningCourses/${id}/Media`,
		});
	}

	static GetCourseEducationQuestions(id: number): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `ELearningCourses/${id}/Questions`,
		});
	}

	static GetCourseExamResults(id: number): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `ELearningCourses/${id}/ExamResults`,
		});
	}

	static GetCourseEducationQuestion(id: number): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `ExamQuestions/${id}`,
		});
	}

	public static CreateShiftName(request: ShiftNameRequest) {
		return DataAdapter.Post<ShiftName>("ShiftNames", request);
	}

	public static CreateShiftCrew(request: NameColorPayload) {
		const name = request?.Name ?? request?.name ?? "";
		const color = request?.Color ?? request?.color ?? "";

		const params = new URLSearchParams();

		if (name) params.append("name", name);
		if (color) params.append("color", color);

		const qs = params.toString() ? `?${params.toString()}` : "";

		return DataAdapter.Post<ShiftEventCrew>(`ShiftCrew${qs}`, null);
	}

	public static UpdateShiftName(id: number, request: ShiftNameRequest) {
		return DataAdapter.Put<ShiftSystem>(`ShiftNames/${id}`, request);
	}

	public static UpdateShiftCrew(id: number, request: NameColorPayload) {
		if (!id) throw new Error("Missing ShiftCrew id");

		const name = request?.Name ?? request?.name ?? "";
		const color = request?.Color ?? request?.color ?? "";

		const params = new URLSearchParams();

		if (name) params.append("name", name);
		if (color) params.append("color", color);

		const qs = params.toString() ? `?${params.toString()}` : "";

		return DataAdapter.Put<ShiftEventCrew>(`ShiftCrew/${id}${qs}`, null);
	}

	public static DeleteShiftName(id: number) {
		return DataAdapter.Delete(`ShiftNames/${id}`);
	}

	public static DeleteShiftCrew(id: number) {
		return DataAdapter.Delete(`ShiftCrew/${id}`);
	}

	public static GetShiftSystems(): Promise<ShiftSystem[]> {
		return DataAdapter.Get<ShiftSystem[]>({
			endpoint: `ShiftSystems`,
		});
	}

	public static GetShiftCrews(): Promise<ShiftEventCrew[]> {
		return DataAdapter.Get<ShiftEventCrew[]>({
			endpoint: `ShiftCrew`,
		});
	}

	public static GetMeasurementsDaily(): Promise<MeasurementsDaily[]> {
		return DataAdapter.Get<MeasurementsDaily[]>({
			endpoint: `LabModule/measurementdaily`,
		});
	}

	public static GetMeasurementsDailySaveClear(): Promise<MeasurementsDaily[]> {
		return DataAdapter.Get<MeasurementsDaily[]>({
			endpoint: `LabModule/measurementdailypage`,
		});
	}

	public static GetMeasurementsSpot(): Promise<MeasurementsDaily[]> {
		return DataAdapter.Get<MeasurementsDaily[]>({
			endpoint: `LabModule/measurementspot`,
		});
	}

	public static GetLabGroups(): Promise<unknown[]> {
		return DataAdapter.Get<unknown[]>({
			endpoint: `LabModule/labgroups`,
		});
	}

	public static GetLabSystems(): Promise<unknown[]> {
		return DataAdapter.Get<unknown[]>({
			endpoint: `LabModule/labpsystems`,
		});
	}

	public static GetLabParams(): Promise<unknown[]> {
		return DataAdapter.Get<unknown[]>({
			endpoint: `LabModule/labparameters`,
		});
	}

	public static GetLabReportPage(): Promise<unknown[]> {
		return DataAdapter.Get<unknown[]>({
			endpoint: `LabModule/measurementarchive/0`,
		});
	}

	static GetLabReportPageId(request: IdRequest): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/measurementarchive/${request.id}`,
		});
	}

	static GetMeasurementById(request: IdRequest): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/measurement/${request.id}`,
		});
	}

	public static SaveSearch(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(`LabModule/measurements`, request);
	}

	public static UpdateLabGroup(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(`LabModule/updatelabgroup`, request);
	}

	public static UpdateLabAnalyze(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(`LabModule/updatelabanalyze`, request);
	}

	public static CreateTaskComment(
		comment: TaskAddCommentRequest,
	): Promise<TaskCommentResponse> {
		return DataAdapter.PostForm<TaskCommentResponse>(`TaskComments`, comment);
	}

	public static CreateTask(task: TaskSave): Promise<TaskResponse> {
		return DataAdapter.PostFormObject<TaskResponse>(`Tasks`, task);
	}

	public static UpdateTask(
		taskId: number,
		payload: TaskSave,
	): Promise<TaskResponse> {
		return DataAdapter.PutFormObject<TaskResponse>(`Tasks/${taskId}`, payload);
	}

	public static UpdateMeasurementGroup(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(
			`LabModule/updatemeasurementgroup`,
			request,
		);
	}

	public static UpdateLearningCourse(
		request: ELearningCourseUpdateRequestPayload,
	) {
		return DataAdapter.Put<unknown>(`ELearningCourses/${request.id}`, request);
	}

	public static AddLearningCourse(request: ELearningCourseRequestPayload) {
		return DataAdapter.Post<unknown>(`ELearningCourses`, request);
	}

	public static AddNewQuestionToCourse(
		request: AddELearningQuestionRequestPayload,
	) {
		return DataAdapter.Post<unknown>(
			`ELearningCourses/${request.courseId}/Questions`,
			request,
		);
	}

	public static AddExam(request: SubmitExamRequestPayload) {
		return DataAdapter.Post<unknown>(`Exams`, request);
	}

	public static AddPutExam(request: PutExamRequestPayload) {
		return DataAdapter.Put<unknown>(`Exams/${request.Id}`, request);
	}

	public static GetLabReports(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(`LabModule/getLabReports`, request);
	}

	public static PostLabArchive(request: Record<string, unknown>) {
		return DataAdapter.Post<unknown>(`LabModule/createarchive`, request);
	}

	public static CreateComment(
		request: MeasurementCommentRequestPayload,
	): Promise<MeasurementCommentResponsePayload> {
		return DataAdapter.PostForm<MeasurementCommentResponsePayload>(
			`LabModule/addcomment`,
			request,
		);
	}

	static GetMeasurementAnalyzeById(request: IdRequest): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/measurementanalyze/${request.id}`,
		});
	}

	static GetMeasurementByGroupId(request: IdRequest): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/measurementgroup/${request.id}`,
		});
	}

	static GetLabAnalyzeListById(request: IdRequest): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/labanalyzelist/${request.id}`,
		});
	}

	static GetLabAnalyzeById(request: IdRequest): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/labanalyze/${request.id}`,
		});
	}

	static GetFetchLabGroupsMeasurementPage(
		request: IdRequest,
	): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `LabModule/measurementpage/${request.id}`,
		});
	}

	public static CreateShiftSystem(request: ShiftSystemRequest) {
		return DataAdapter.Post<ShiftSystem>("ShiftSystems", request);
	}

	public static UpdateShiftSystem(id: number, request: ShiftSystemRequest) {
		return DataAdapter.Put<ShiftSystem>(`ShiftSystems/${id}`, request);
	}

	static DeleteShiftSystem(id: number) {
		return DataAdapter.Delete(`ShiftSystems/${id}`);
	}

	static GetShiftSettings(): Promise<ShiftSettingsResponse> {
		return DataAdapter.Get<ShiftSettingsResponse>({
			endpoint: `Settings/Organization/Shift`,
		});
	}

	static UpdateShiftSettings(
		request: ShiftSettingsRequest,
	): Promise<ShiftSettingsResponse> {
		return DataAdapter.Put<ShiftSettingsResponse>(
			"Settings/Organization/Shift",
			request,
		);
	}

	static GetShiftOptions(): Promise<ShiftOptions> {
		return DataAdapter.Get<ShiftOptions>({
			endpoint: `shift/events/create-new-options`,
		});
	}

	static GetTaskGroupsByStatus(statuses: string[]): Promise<TaskGroup[]> {
		const query = statuses
			.map((s) => `status=${encodeURIComponent(s)}`)
			.join("&");

		return DataAdapter.Get<TaskGroup[]>({
			endpoint: `Group/Task?${query}`,
		});
	}

	static GetTaskGroupsAPI(): Promise<TaskGroup[]> {
		return DataAdapter.Get<TaskGroup[]>({
			endpoint: `user/me/TaskGroups`,
		});
	}

	static GetShiftEvents(
		request: ShiftEventsQuery,
	): Promise<ShiftEventsPagedResponse> {
		const endpoint = buildEndpointWithQueryParameters("ShiftEvents", request);
		return DataAdapter.Get<ShiftEventsPagedResponse>({ endpoint });
	}

	static GetShiftEvent(shiftEventId: number): Promise<ShiftEventResponse> {
		return DataAdapter.Get<ShiftEventResponse>({
			endpoint: `ShiftEvents/${shiftEventId}`,
		});
	}

	public static UpdateShiftEvent(
		shiftLogData: ShiftEventRequest,
		shiftEventId: number,
	): Promise<Record<string, unknown>> {
		return DataAdapter.PutFormObject<Record<string, unknown>>(
			`ShiftEvents/${shiftEventId}`,
			shiftLogData,
		);
	}

	public static CreateShiftEvent(
		shiftEvent: ShiftEventRequest,
	): Promise<ShiftEventResponse> {
		return DataAdapter.PostFormObject<ShiftEventResponse>(
			"ShiftEvents",
			shiftEvent,
		);
	}

	public static UploadShiftEventFile(file: File): Promise<File> {
		return DataAdapter.Post<File>(`shift/events/file-upload`, file);
	}

	public static UploadShiftCommentImage(request: {
		File: File;
		companyId: number;
	}): Promise<string> {
		return DataAdapter.UploadForm<string>(
			`Shift/companies/${request.companyId}/comments/file-upload`,
			request,
		);
	}

	public static CopyUserSettings(request: {
		targetUser: number;
		sourceUser: number;
	}): Promise<void> {
		return DataAdapter.Post<void>(`Group/copygroups`, request);
	}

	public static CreateShiftEventComment(
		request: CreateShiftEventCommentRequest,
	): Promise<Comment> {
		return DataAdapter.Post<Comment>(`ShiftEventComments`, request);
	}

	public static DeleteShiftEvent(shiftEventId: number) {
		return DataAdapter.Delete(`ShiftEvents/${shiftEventId}`);
	}

	public static UpdateGroupSaveClear(
		request: GroupSaveClearRequest,
	): Promise<unknown> {
		return DataAdapter.Patch<unknown>(`Group/${request.groupId}`, request);
	}

	public static UpdateShiftEventStatus(
		request: UpdateShiftEventStatusRequest,
	): Promise<ShiftEventResponse> {
		return DataAdapter.Patch<ShiftEventResponse>(
			`ShiftEvents/${request.shiftId}`,
			request,
		);
	}

	static RefreshSharePointDocument(
		request: RefreshSharePointDocumentRequest,
	): Promise<RefreshSharePointDocumentResponse> {
		return DataAdapter.Post<RefreshSharePointDocumentResponse>(
			`documents/sharepoint/${request.ExternalUniqueId}`,
			request,
		);
	}

	static CreateNode(
		request: CreateNodeRequest,
	): Promise<ProcedureDetailNode> {
		return DataAdapter.Post<ProcedureDetailNode>(`nodes`, request);
	}

	static UploadProcedureNodeDocument(
		request: UploadProcedureNodeDocumentRequest,
	): Promise<UploadProcedureNodeDocumentResponse> {
		return DataAdapter.UploadForm(
			`nodes/${request.SchemeId}/document`,
			request,
		);
	}

	static GetNodeDocument(request: {
		ProcedureId: number;
		SchemeId: number;
	}): Promise<DocumentUsedForNodeResponse> {
		return DataAdapter.Get<DocumentUsedForNodeResponse>({
			endpoint: `Nodes/${request.SchemeId}/Document?ProcedureId=${request.ProcedureId}`,
		});
	}

	static UpdateProcedureNodeDocument(
		request: ImportProcedureNodeDocumentRequest,
	): Promise<UpdateProcedureNodeDocumentResponse> {
		return DataAdapter.PutForm<UpdateProcedureNodeDocumentResponse>(
			`nodes/${request.SchemeId}/document`,
			request,
		);
	}

	static UpdateProcedureNode(
		request: UpdateProcedureNodeRequest,
	): Promise<UpdateProcedureNodeResponse> {
		return DataAdapter.Put<UpdateProcedureNodeResponse>(
			`nodes/${request.SchemeId}`,
			request,
		);
	}

	static GetProcedureDocuments(
		searchTerm: string,
	): Promise<DocumentForProcedureImport[]> {
		const queryParam = searchTerm ? `?searchTerm=${searchTerm}` : "";
		return DataAdapter.Get<DocumentForProcedureImport[]>({
			endpoint: `Documents${queryParam}`,
		});
	}

	static GetActiveEmailJobs(): Promise<ActiveCompetencyEmailJobConfiguration> {
		return DataAdapter.Get<ActiveCompetencyEmailJobConfiguration>({
			endpoint: "competence/job/email/active",
		});
	}

	static ActivateCompetencyTestEmail(
		request: ActivateCompetencyTestEmailRequest,
	): Promise<ActivateCompetencyTestEmailResponse> {
		return DataAdapter.Post<ActivateCompetencyTestEmailResponse>(
			"competence/job/email/recipients",
			request,
		);
	}

	static ChangeUserOrganization(
		request: ChangeUserOrganizationRequest,
	): Promise<ChangeUserOrganizationResponse> {
		return DataAdapter.Put<ChangeUserOrganizationResponse>(
			"user/me/companies",
			request,
		);
	}

	static GetUserCompanies(): Promise<UserCompaniesResponse> {
		return DataAdapter.Get<UserCompaniesResponse>({
			endpoint: "user/me/companies",
		});
	}

	static GetUsers(companyId?: number): Promise<UserSummaryResponse[]> {
		const queryParam = companyId ? `?companyId=${companyId}` : "";
		return DataAdapter.Get<UserSummaryResponse[]>({
			endpoint: `user${queryParam}`,
		});
	}

	static GetUser(userId: number): Promise<User> {
		return DataAdapter.Get<User>({
			endpoint: `user/${userId}`,
		});
	}

	static CreateCompetencyEmailJob(
		request: CreateCompetencyEmailJobRequest,
	): Promise<CreateCompetencyEmailJobResponse> {
		return DataAdapter.Post<CreateCompetencyEmailJobResponse>(
			"competence/job/email",
			request,
		);
	}

	static ApproveUpdateMaterialHandlingVersion(
		request: MaterialHandlingApproveVersionRequest,
	): Promise<MaterialHandlingApproveVersionResponse> {
		return DataAdapter.Post<MaterialHandlingApproveVersionResponse>(
			`MaterialHandlingProcedures/${request.MaterialHandlingId}/ApproveVersion`,
			request,
		);
	}

	static Search(searchTerm: string): Promise<SearchResponse> {
		return DataAdapter.Get<SearchResponse>({
			endpoint: `procedure/search/${searchTerm}`,
		});
	}

	static GetProcedureShiftConfiguration(): Promise<ShiftConfigurationResponse> {
		return DataAdapter.Get<ShiftConfigurationResponse>({
			endpoint: "procedure/shift/configuration",
		});
	}

	static GetProcedureStepLog(
		id: string,
		checklistId: number | undefined,
		stepId: number,
	): Promise<ChecklistStepLog> {
		return DataAdapter.Get<ChecklistStepLog>({
			endpoint: `ChecklistSteps/${stepId}/log`,
			queryParams: `?ProcedureId=${id}&ChecklistId=${checklistId}`,
		});
	}

	static GetProcedureSuggestionByChecklistId(
		checklistId: number | undefined,
	): Promise<unknown> {
		return DataAdapter.Get<unknown>({
			endpoint: `Checklist/${checklistId}/ProcedureUpdateSuggestions`,
		});
	}

	static GetCompetenceUserList(): Promise<CompetenceUserResult> {
		return DataAdapter.Get<CompetenceUserResult>({
			endpoint: "competence/list/user",
		});
	}

	static GetLiveTags(): Promise<LiveTag[]> {
		return DataAdapter.Get<LiveTag[]>({
			endpoint: "SatosLive/Tags",
		});
	}

	static SearchLiveTags(search: string): Promise<LiveTag[]> {
		return DataAdapter.Get<LiveTag[]>({
			endpoint: `SatosLive/Tags/Search/${search}`,
		});
	}

	static GetMultipleLiveTagsCurrentValue(
		request: string[],
	): Promise<LiveTagValue[]> {
		return DataAdapter.Post<LiveTagValue[]>(
			"SatosLive/Tags/datapoints/multiple",
			request,
		);
	}

	static GetEditableLiveTag(id: string): Promise<EditableLiveTag> {
		return DataAdapter.Get<EditableLiveTag>({
			endpoint: `SatosLive/DemoData/${id}`,
		});
	}

	static UpdateLiveTag(
		id: string,
		request: UpdateLiveTagRequest,
	): Promise<EditableLiveTag> {
		return DataAdapter.Put<EditableLiveTag>(
			`SatosLive/DemoData/${id}`,
			request,
		);
	}

	static GetJobsStatus(request: JobStatusRequest): Promise<JobStatusResponse> {
		return DataAdapter.Post<JobStatusResponse>("status/jobs", request);
	}

	static GetTasks(
		groupIds: number[],
		status: string,
		page: number,
		pageSize: number,
		searchTermTable?: string,
		sort?: SortDirection,
		recurringTemplateId?: number,
	): Promise<TaskResponse> {
		const query = new URLSearchParams();

		groupIds.forEach((id) => query.append("GroupIds", id.toString()));
		if (status) query.append("Status", status);
		if (typeof recurringTemplateId === "number" && recurringTemplateId > 0) {
			query.append("RecurringTemplateId", recurringTemplateId.toString());
		}
		if (searchTermTable) query.append("Keyword", searchTermTable);
		query.append("Page", page.toString());
		query.append("PageSize", pageSize.toString());
		if (sort) query.append("Sort", String(sort));

		return DataAdapter.Get<TaskResponse>({
			endpoint: `Tasks?${query.toString()}`,
		});
	}

	static GetProcedureUpdate(
		hasMaterialHandling: boolean,
	): Promise<ProcedureUpdateSuggestionsResponse[]> {
		return DataAdapter.Get<ProcedureUpdateSuggestionsResponse[]>({
			endpoint: hasMaterialHandling
				? "procedureUpdateSuggestions/MaterialHandling"
				: "procedureUpdateSuggestions",
		});
	}

	static SearchMaterialHandlingTag(
		searchTerm: string,
	): Promise<MaterialHandlingSearchResponse> {
		return DataAdapter.Get<MaterialHandlingSearchResponse>({
			endpoint: `MaterialHandlingProcedures/Search/${searchTerm}`,
		});
	}

	static GetCompanyCompetenceGroups(
		companyId: number | undefined,
	): Promise<CompanyCompetenceGroupResponse> {
		return DataAdapter.Get<CompanyCompetenceGroupResponse>({
			endpoint: `competence/company/${companyId}/groups`,
		});
	}

	static GetUserCompetenceDocumentStatus(
		request: UserCompetenceDocumentStatusRequest,
	): Promise<UserCompetenceDocumentStatusResponse> {
		return DataAdapter.Post<UserCompetenceDocumentStatusResponse>(
			"competence/user/document/status",
			request,
		);
	}

	static GetProcedureGroupDetails(
		id: number | undefined,
	): Promise<ProcedureGroup> {
		return DataAdapter.Get<ProcedureGroup>({
			endpoint: `proceduregroups/details/${id}`,
		});
	}

	public static GetCurrentResource(
		id: string | undefined,
	): Promise<ResourceResponse> {
		return DataAdapter.Get<ResourceResponse>({
			endpoint: `resource/download/${id}`,
		});
	}

	public static GetCurrentResourceDetails(
		id: string | undefined,
	): Promise<ResourceResponse> {
		return DataAdapter.Get<ResourceResponse>({
			endpoint: `resource/details/${id}`,
		});
	}

	public static GetCurrentResourceDetailsByFileId(
		request: ResourceRequestBase | undefined,
	): Promise<ResourceResponse> {
		return DataAdapter.Post<ResourceResponse>("resource/details/", request);
	}

	public static UpdateUserCompetenceUserDocumentStatus(
		request: UpdateUserCompetenceDocumentStatusRequest | undefined,
	): Promise<UpdateUserCompetenceDocumentStatusResponse> {
		return DataAdapter.Post<UpdateUserCompetenceDocumentStatusResponse>(
			"competence/user/document/update",
			request,
		);
	}

	public static GetCompetenceMatrix(id: string): Promise<CompetenceMatrix> {
		return DataAdapter.Get<CompetenceMatrix>({
			endpoint: `competence/competencematrix/${id}`,
		});
	}

	public static QueryArchiveUsers(): Promise<ArchiveUsersResponse> {
		return DataAdapter.Post<ArchiveUsersResponse>("archive/users", {});
	}

	public static QueryArchivedProcedure(
		procedureId: string,
	): Promise<ArchiveProject> {
		return DataAdapter.Get<ArchiveProject>({
			endpoint: `archive/completed/${procedureId}`,
		});
	}

	public static QueryArchivedProcedures(
		request: ArchivedProjectsRequest,
	): Promise<ArchivedProjectsResponse> {
		return DataAdapter.Post<ArchivedProjectsResponse>(
			"archive/project/list",
			request,
		);
	}

	public static CreateArchivedProcedurePdf(procedureId: number) {
		return DataAdapter.Get({
			endpoint: `archive/completed/${procedureId}/pdf`,
		});
	}

	public static GetCurrentUser(): Promise<User> {
		return DataAdapter.Get<User>({ endpoint: "user/me" });
	}

	public static GetApplicationConfiguration(): Promise<ApplicationConfiguration> {
		return DataAdapter.Get({ endpoint: "Application" });
	}

	public static GetCompanies() {
		return DataAdapter.Get<Company[]>({
			endpoint: "Company",
		});
	}

	public static UserCompanyConfiguration() {
		return DataAdapter.Get<UserCompanyConfiguration>({
			endpoint: "usercompanyconfiguration",
		});
	}

	public static CreateOrganization(
		request: CreateCompanyRequest,
	): Promise<Company> {
		return DataAdapter.Post<Company>("Company", request);
	}

	public static GetActiveDirectorySettings() {
		return DataAdapter.Get<ActiveDirectorySettings>({
			endpoint: "settings/organization/activeDirectory",
		});
	}

	public static UpdateActiveDirectorySettings(
		activeDirectorySettings: ActiveDirectorySettings,
	) {
		return DataAdapter.Put<ActiveDirectorySettings>(
			"settings/organization/activeDirectory",
			activeDirectorySettings,
		);
	}

	public static GetOrganizationSettings() {
		return DataAdapter.Get<GeneralFormData>({
			endpoint: "settings/organization/general",
		});
	}

	public static UpdateOrganizationSettings(
		organizationSettings: OrganizationSettingsRequest,
	) {
		return DataAdapter.Put<OrganizationSettingsRequest>(
			"settings/organization/general",
			organizationSettings,
		);
	}

	public static GetSystemSettings() {
		return DataAdapter.Get<SystemSettingsResponse>({
			endpoint: "settings/system",
		});
	}

	public static CreateSystemSettings(request: CreateSystemSettingsRequest) {
		return DataAdapter.Post<SystemSettingsResponse>("settings/system", request);
	}

	public static GetAzureAdGroupSyncLogs() {
		return DataAdapter.Get<AzureAdGroupSyncLogEntry[]>({
			endpoint: "Settings/System/AzureAdGroupSyncLogs",
		});
	}

	public static GetLabParameters() {
		return DataAdapter.Get<LabParameter[]>({
			endpoint: "LabParameters",
		});
	}
	public static CreateLabParameter(request: LabParameterRequest) {
		return DataAdapter.Post<LabParameter>("LabParameters", request);
	}

	public static UpdateLabParameter(id: number, request: LabParameterRequest) {
		return DataAdapter.Put<LabParameter>(`LabParameters/${id}`, request);
	}

	public static DeleteLabParameter(id: number) {
		return DataAdapter.Delete(`LabParameters/${id}`);
	}

	public static GetLabSettings() {
		return DataAdapter.Get<LabSettings>({
			endpoint: "settings/organization/lab",
		});
	}

	public static UpdateLabSettings(request: LabSettings) {
		return DataAdapter.Put<LabSettings>("settings/organization/lab", request);
	}

	public static GetMaterialHandlingSettings() {
		return DataAdapter.Get<MaterialHandlingSettings>({
			endpoint: "settings/organization/MaterialHandling",
		});
	}

	public static UpdateMaterialHandlingSettings(
		setting: MaterialHandlingSettings,
	) {
		return DataAdapter.Put<MaterialHandlingSettings>(
			"settings/organization/MaterialHandling",
			setting,
		);
	}

	public static GetTimezones() {
		return DataAdapter.Get<Timezones[]>({
			endpoint: "settings/timezones",
		});
	}

	public static GetBrandingSettings() {
		return DataAdapter.Get<BrandingSettingsType>({
			endpoint: "settings/organization/Branding",
		});
	}

	public static UpdateBrandingSettings(
		brandingSettings: BrandingSettingsRequest,
	): Promise<BrandingSettingsType> {
		return DataAdapter.Put<BrandingSettingsType>(
			"settings/organization/Branding",
			brandingSettings,
		);
	}

	public static UploadStartPageImage(
		request: StartPageImageRequest,
	): Promise<StartPageImageResponse> {
		return DataAdapter.UploadImage<StartPageImageResponse>(
			"settings/Organization/Branding/StartPageImage",
			request,
		);
	}

	public static GetProceduresSettings() {
		return DataAdapter.Get<ProceduresSettings>({
			endpoint: "settings/organization/procedure",
		});
	}

	public static UpdateProcedureSettings(
		procedureSettings: ProceduresSettingsRequestType,
	): Promise<ProceduresSettingsRequestType> {
		return DataAdapter.Put<ProceduresSettingsRequestType>(
			"Settings/Organization/Procedure",
			procedureSettings,
		);
	}

	public static OrganizationModules(orgId: number) {
		return DataAdapter.Get<OrganizationModules>({
			endpoint: `organizationmodules/${orgId}`,
		});
	}

	public static UpdateOrganizationModules(
		request: OrganizationModulesRequest,
	): Promise<OrganizationModules> {
		return DataAdapter.Patch<OrganizationModules>(
			"organizationmodules",
			request,
		);
	}

	public static GetLanguageSettings(
		request: GetTranslationsRequest,
	): Promise<PagedResponse<LanguageSettingsType>> {
		const endpoint = buildEndpointWithQueryParameters(
			"language/settings",
			request,
		);
		return DataAdapter.Get<PagedResponse<LanguageSettingsType>>({ endpoint });
	}

	public static UpdateLanguageSettings(
		languageSettings: LanguageSettingsRequestType,
	) {
		return DataAdapter.Put<LanguageSettingsRequestType>(
			`language/settings/${languageSettings.Id}`,
			languageSettings,
		);
	}

	public static CreateNewLanguageSettings(
		languageSettings: CreateNewLanguageSettingsRequestType,
	) {
		return DataAdapter.Post<CreateNewLanguageSettingsRequestType>(
			"language/settings",
			languageSettings,
		);
	}

	public static CreateLanguageSettingsBatch(
		languageSettings: CreateNewLanguageSettingsRequestType[],
	) {
		return DataAdapter.Post<CreateNewLanguageSettingsRequestType[]>(
			"language/Settings/Batch",
			languageSettings,
		);
	}

	public static GetTaskSettings() {
		return DataAdapter.Get<TaskSettingsResponse>({
			endpoint: "settings/organization/task",
		});
	}

	public static UpdateTaskSettings(request: TaskSettingsRequest) {
		return DataAdapter.Put<TaskSettingsRequest>(
			"settings/organization/task",
			request,
		);
	}

	public static GetServerEmailSettings() {
		return DataAdapter.Get<ServerEmailSettingsResponse>({
			endpoint: "settings/system/email",
		});
	}

	public static UpdateServerEmailSettings(request: ServerEmailSettingsRequest) {
		return DataAdapter.Put<ServerEmailSettingsRequest>(
			"settings/system/email",
			request,
		);
	}

	public static GetServerPdfConversionSettings() {
		return DataAdapter.Get<ServerPdfConversionSettingsResponse>({
			endpoint: "Settings/System/PdfConversion",
		});
	}

	public static UpdateServerPdfConversionSettings(
		request: ServerPdfConversionSettingsRequest,
	) {
		return DataAdapter.Put<ServerPdfConversionSettingsRequest>(
			"Settings/System/PdfConversion",
			request,
		);
	}

	public static GetServerWordConvertorSettings() {
		return DataAdapter.Get<ServerWordConvertorSettingsResponse>({
			endpoint: "Settings/System/WordConvertor",
		});
	}

	public static UpdateServerWordConvertorSettings(
		request: ServerWordConvertorSettingsRequest,
	) {
		return DataAdapter.Put<ServerWordConvertorSettingsRequest>(
			"Settings/System/WordConvertor",
			request,
		);
	}

	public static TriggerUserGroupMembershipUpdate(): Promise<number> {
		return DataAdapter.PutWithNoPayload("user/GroupMemberships");
	}

	public static GetSystemInformation() {
		return DataAdapter.Get<SystemInformation>({
			endpoint: "Settings/System/Information",
		});
	}

	public static GetGroups() {
		return DataAdapter.Get<Group[]>({
			endpoint: "group",
		});
	}

	public static GetGroup(groupId: number): Promise<Group> {
		return DataAdapter.Get<Group>({
			endpoint: `group/${groupId}`,
		});
	}

	public static GetUserGroupMembershipStatus(
		groupId: number,
	): Promise<GetUserGroupMembershipStatusResponse> {
		return DataAdapter.Get<GetUserGroupMembershipStatusResponse>({
			endpoint: `group/${groupId}/membership`,
		});
	}

	public static CreateGroup(group: GroupRequestType): Promise<Group> {
		return DataAdapter.Post<Group>(`group`, group);
	}

	public static UpdateGroup(group: GroupRequestType, id: number) {
		return DataAdapter.Put<Group>(`group/${id}`, group);
	}

	public static UpdateUser(user: Partial<User>, id: number) {
		return DataAdapter.Patch(`user/${id}`, user);
	}

	public static DeleteGroup(groupId: number) {
		return DataAdapter.Delete(`group/${groupId}`);
	}

	public static UpdateGroupMembership(
		request: GroupMembershipType,
		userId: number,
		groupId: number,
	) {
		return DataAdapter.Put<GroupMembershipType>(
			`user/${userId}/groups/${groupId}/membershipType`,
			request,
		);
	}

	public static GetGroupResources(groupId: number): Promise<GroupResources> {
		return DataAdapter.Get<GroupResources>({
			endpoint: `group/${groupId}/resources`,
		});
	}

	public static updateGroupResource(
		request: GroupResourceRequest,
		groupId: number,
		resourceType: GroupResourceType,
	) {
		return DataAdapter.Patch<GroupResource>(
			`group/${groupId}/resources/${resourceType}/${request.Id}`,
			request,
		);
	}

	public static updateTargetTime(
		request: Record<string, unknown>,
		ChecklistId: number,
	) {
		return DataAdapter.Put<GroupResource>(
			`checklist/${ChecklistId}/targetDuration/`,
			request,
		);
	}

	public static GetProcedure(id: number): Promise<Procedure> {
		return DataAdapter.Get<Procedure>({
			endpoint: `procedure/${id}`,
		});
	}

	public static GetNode(
		procedureId: number,
		nodeId: number,
	): Promise<ProcedureDetails> {
		return DataAdapter.Get<ProcedureDetails>({
			endpoint: `nodes/${nodeId}`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetProcedureDetails<ProcedureDetails>(
		procedureId: string | number,
	): Promise<ProcedureDetails> {
		return DataAdapter.Get<ProcedureDetails>({
			endpoint: `procedure/${procedureId}/details`,
		});
	}

	public static GetCommentsByChecklistId(
		procedureId: number,
		checklistId: number,
	): Promise<Record<number, ProcedureNodeStepComment[]>> {
		return DataAdapter.Get<Record<number, ProcedureNodeStepComment[]>>({
			endpoint: `checklist/${checklistId}/comments`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetProcedureGroups(filter: string): Promise<ProcedureGroup[]> {
		return DataAdapter.Get<Array<ProcedureGroup>>({
			endpoint: "proceduregroups",
			odataParams: filter,
		});
	}

	public static GetProcedures(filter: string): Promise<Procedure[]> {
		return DataAdapter.Get<Array<Procedure>>({
			endpoint: "procedures",
			odataParams: filter,
		});
	}

	public static GetActiveProcedures(
		page: number,
		pageSize: number,
		search?: string,
		sort?: "ASC" | "DESC",
	) {
		const query = new URLSearchParams({
			Page: page.toString(),
			PageSize: pageSize.toString(),
		});

		if (search) {
			query.append("Search", search);
		}
		if (sort) {
			query.append("Sort", sort);
		}

		return DataAdapter.Get<PagedResponse<ActiveProcedure>>({
			endpoint: "procedure/active",
			queryParams: `?${query.toString()}`,
		});
	}

	public static GetMaterialHandlingChecklistForEdit(
		procedureId: number,
		checklistId: number | undefined,
	): Promise<Checklist> {
		return DataAdapter.Get<Checklist>({
			endpoint: `checklist/${checklistId}/MaterialHandlingForEdit`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetReviewChecklist(
		procedureId: number | undefined,
		isUpdatedChecklist: boolean,
		nodeId: number | undefined,
		checklistId: number | undefined,
	): Promise<Checklist> {
		return DataAdapter.Get<Checklist>({
			endpoint: `checklist/${checklistId}/review`,
			queryParams: `?ProcedureId=${procedureId}&NodeId=${nodeId}&IsUpdatedChecklist=${isUpdatedChecklist}`,
		});
	}

	public static GetChecklistStepStatuses(
		procedureId: number,
		checklistId: number | string | undefined,
	): Promise<StepStatus[]> {
		return DataAdapter.Get<StepStatus[]>({
			endpoint: `checklist/${checklistId}/stepStatuses`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetChecklist(
		procedureId: number | string | undefined,
		checklistId: number | string | undefined,
	): Promise<Checklist> {
		return DataAdapter.Get<Checklist>({
			endpoint: `checklist/${checklistId}`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static UpsertChecklist(
		data: QueryUpsertProcedureNodeSteps,
	): Promise<QueryUpsertProcedureNodeSteps> {
		return DataAdapter.Put<QueryUpsertProcedureNodeSteps>(
			`checklist/${data.ChecklistId}`,
			data,
		);
	}

	public static GetProcedureMetaData(
		filter: string,
		signal: AbortSignal,
	): Promise<ProcedureMetaData> {
		return DataAdapter.Get<ProcedureMetaData>({
			endpoint: "ProceduresMetadata",
			odataParams: filter,
			signal: signal,
		});
	}

	public static GetProcedureMetaDataFolders(
		companyId: number | undefined,
	): Promise<ProcedureMetaData> {
		return DataAdapter.Get<ProcedureMetaData>({
			endpoint: "newproceduresmetadata",
			queryParams: `${companyId}/folders`,
		});
	}

	public static CreateProcedure(
		procedure: CreateNewProcedureRequest,
	): Promise<Procedure> {
		return DataAdapter.Post<Procedure>("procedure", procedure);
	}

	public static UpdateNodeTreePosition(
		request: ProcedureAccordionMove,
	): Promise<number> {
		return DataAdapter.Put<number>(
			`nodes/${request.SchemeId}/TreePosition`,
			request,
		);
	}

	public static UpdateProcedureSetting(
		procedureSetting: EditProcedure,
	): Promise<number> {
		if (procedureSetting.File !== null) {
			return DataAdapter.UploadForm<number>(
				"procedure/SettingsWithFile",
				procedureSetting,
			);
		}

		return DataAdapter.Post<number>("procedure/Settings", procedureSetting);
	}

	public static async DeleteProcedure(request: DeleteProcedureRequest) {
		return DataAdapter.Delete(`procedure/${request.procedureId}`);
	}

	public static MergeFileLinks(FileId: number): Promise<number> {
		return DataAdapter.Post<number>("procedure/mergefilelinks", FileId);
	}

	public static GetProcedureKnowledgeStatus(
		procedureId: number | undefined,
	): Promise<UserProcedureKnowledge> {
		return DataAdapter.Get({
			endpoint: "Competence",
			queryParams: `getprocedureknowledgestatus/${procedureId}`,
		});
	}

	public static PostProcedureUpdateNotice(
		updateNotice: ProcedureUpdateNoticeRequest,
	): Promise<unknown> {
		return DataAdapter.Post<unknown>(
			"Competence/setprocedureupdatenotice",
			updateNotice,
		);
	}

	public static PostProcedureKnowledgeStatus(
		procedureKnowledge: Record<string, unknown>,
	): Promise<unknown> {
		return DataAdapter.Post<unknown>(
			"Competence/setnewprocedureknowledge",
			procedureKnowledge,
		);
	}

	public static SaveAndResetProcedure(
		save: SaveProcedureRequest,
	): Promise<SaveProcedureResponse> {
		return DataAdapter.Post<SaveProcedureResponse>(
			`procedure/${save.ProcedureId}/save`,
			save,
		);
	}

	public static UpdateStepStatus(
		stepStatusUpdate: UpdateStepStatusRequest,
	): Promise<UpdateStepStatusResponse> {
		if (stepStatusUpdate.File !== null && stepStatusUpdate.File !== undefined) {
			return DataAdapter.UploadFormPatch<UpdateStepStatusResponse>(
				`ChecklistSteps/${stepStatusUpdate.StepId}/StatusWithFile`,
				stepStatusUpdate,
			);
		}

		return DataAdapter.Patch<UpdateStepStatusResponse>(
			`ChecklistSteps/${stepStatusUpdate.StepId}/Status`,
			stepStatusUpdate,
		);
	}

	public static PostProcedureUpdateSuggestion(
		suggestion: SuggestionRequest,
	): Promise<SuggestionResponse> {
		if (suggestion.Attachment !== null) {
			return DataAdapter.UploadForm<SuggestionResponse>(
				"ProcedureUpdateSuggestions",
				suggestion,
			);
		}

		return DataAdapter.Post<SuggestionResponse>(
			"ProcedureUpdateSuggestions",
			suggestion,
		);
	}

	public static PutProcedureUpdateSuggestion(
		suggestionId: number,
		suggestion: SuggestionRequest,
	): Promise<SuggestionRequest> {
		if (suggestion.Attachment !== null) {
			return DataAdapter.PutForm<SuggestionRequest>(
				`ProcedureUpdateSuggestions/${suggestionId}`,
				suggestion,
			);
		}

		return DataAdapter.PutForm<SuggestionRequest>(
			`ProcedureUpdateSuggestions/${suggestionId}`,
			suggestion,
		);
	}

	public static AddProcedureUpdateSuggestionComment(
		comment: Record<string, unknown>,
	): Promise<SuggestionResponse> {
		return DataAdapter.Post<SuggestionResponse>(
			`ProcedureUpdateSuggestions/addcommeent`,
			comment,
		);
	}

	public static DeleteProcedureUpdateSuggestionComment(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`ProcedureUpdateSuggestions/deletecomment/${id}`,
		);
	}

	public static DeleteLabMeasurement(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`LabModule/labmeasurement/${id}`,
		);
	}

	public static DeleteLabGroup(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`LabModule/deletelabgroup/${id}`,
		);
	}

	public static DeleteTaskEvent(id: number) {
		return DataAdapter.Delete<unknown>(`Tasks/${id}`);
	}

	public static DeleteTaskEventComment(commentId: number, taskId: number) {
		const queryString = `TaskComments/${commentId}?taskId=${taskId}`;
		return DataAdapter.Delete<unknown>(queryString);
	}

	public static CompleteTaskEvent(
		id: number,
		payload: Record<string, unknown>,
	) {
		return DataAdapter.Post<unknown>(`Tasks/${id}/Complete`, payload);
	}

	public static DeleteLabAnalyze(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`LabModule/labanalyze/${id}`,
		);
	}

	public static DeleteCourse(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`ELearningCourses/${id}`,
		);
	}

	public static AddMedia(courseId: number, payload: FormData) {
		return DataAdapter.AddUploadFormMedia<unknown>(
			`ELearningCourses/${courseId}/Media`,
			payload,
		);
	}

	public static UpdateMedia(mediaId: number, payload: FormData) {
		return DataAdapter.PutUploadFormMedia<unknown>(
			`ELearningMedia/${mediaId}`,
			payload,
		);
	}

	public static UpdateExamQuestion(payload: UpdateExamQuestionPayload) {
		return DataAdapter.Put<unknown>(`ExamQuestions/${payload.id}`, payload);
	}

	public static DeleteMedia(id: number) {
		return DataAdapter.Delete<unknown>(`ELearningMedia/${id}`);
	}

	public static DeleteQuestion(id: number) {
		return DataAdapter.Delete<unknown>(`ExamQuestions/${id}`);
	}

	public static UpdateProcedureGroup(save: ProcedureGroup): Promise<number> {
		return DataAdapter.Post<number>(`proceduregroups/update`, save);
	}

	public static UpdateProcedureFolder(
		request: ProcedureGroup,
	): Promise<ProcedureGroup> {
		return DataAdapter.Put("proceduregroups/update/new", request);
	}

	public static GetMaterialHandlingProcedure(
		procedureId: number | undefined,
		materialHandlingTagId: string,
		edit: boolean = false,
	): Promise<MaterialHandlingProcedure> {
		const editParam = edit ? "&Edit=TRUE" : "";

		return DataAdapter.Get<MaterialHandlingProcedure>({
			endpoint: `MaterialHandlingProcedures/${procedureId}`,
			queryParams: `?TagId=${materialHandlingTagId}${editParam}`,
		});
	}

	public static GetMaterialHandlingProcedures(): Promise<MaterialHandlingList> {
		return DataAdapter.Get<MaterialHandlingList>({
			endpoint: "MaterialHandlingProcedures",
			queryParams: ``,
		});
	}

	public static GetMaterialHandlingProceduresWaitingForApproval(): Promise<MaterialHandlingReview> {
		return DataAdapter.Get<MaterialHandlingReview>({
			endpoint: "MaterialHandlingProcedures/WaitingForApproval",
			queryParams: ``,
		});
	}

	public static GetMaterialHandlingArchiveProcedure(
		procedureId: number,
	): Promise<MaterialHandlingArchiveListItem> {
		return DataAdapter.Get<MaterialHandlingArchiveListItem>({
			endpoint: `MaterialHandlingProcedures/${procedureId}/Archive`,
			queryParams: ``,
		});
	}

	public static UpdateMaterialHandlingProcedure(
		request: UpdateMaterialHandlingProcedureRequest,
	): Promise<number> {
		return DataAdapter.Put<number>(
			`MaterialHandlingProcedures/${request.MaterialHandlingId}`,
			request,
		);
	}

	public static ReimportMaterialHandlingProcedure(
		procedureId: number,
	): Promise<number> {
		return DataAdapter.Put<number>(
			`MaterialHandlingProcedures/${procedureId}/ReImport`,
			procedureId,
		);
	}

	public static AddMaterialHandlingProcedure(
		request: CreateMaterialHandlingProcedure,
	): Promise<NewMaterialHandlingProcedureReturn> {
		if (request.File != null) {
			return DataAdapter.UploadForm<NewMaterialHandlingProcedureReturn>(
				"MaterialHandlingProcedures/WithFile",
				request,
			);
		} else {
			return DataAdapter.Post<NewMaterialHandlingProcedureReturn>(
				"MaterialHandlingProcedures",
				request,
			);
		}
	}

	public static AddUnit(newUnit: MaterialHandlingUnit): Promise<number> {
		return DataAdapter.Post<number>("MaterialHandlingElements/Unit", newUnit);
	}

	public static AddNewFastening(
		newFastening: MaterialHandlingFastening,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandlingElements/Fastening",
			newFastening,
		);
	}

	public static AddNewEquipment(
		newEquipment: MaterialHandlingEquipment,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandlingElements/Equipment",
			newEquipment,
		);
	}

	public static AddNewReferenceUrl(
		newReferenceUrl: ReferenceUrl,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandlingElements/ReferenceUrl",
			newReferenceUrl,
		);
	}

	public static DeleteMaterialHandingElement(
		itemToDelete: DeleteMaterialHandling,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandlingElements/Delete",
			itemToDelete,
		);
	}

	public static GetMaterialHandlingGroups(): Promise<GroupList> {
		return DataAdapter.Get<GroupList>({
			endpoint: "Group/MaterialHandling",
			queryParams: ``,
		});
	}

	public static CompleteMaterialHandlingProcedure(
		request: SaveProcedureRequest,
	): Promise<SaveProcedureResponse> {
		return DataAdapter.Post<SaveProcedureResponse>(
			`MaterialHandlingProcedures/${request.ProcedureId}/Complete`,
			request,
		);
	}

	public static GetUserCompetency(fetchUserCompetence: UserCompetenceRequest) {
		return DataAdapter.Post<UserCompetence>(
			"Competence/UserCompetence",
			fetchUserCompetence,
		);
	}

	public static DownloadCompetencyResource(
		request: ResourceRemoteLocationRequest,
	) {
		return DataAdapter.Post<ResourceResponse>(
			"resource/upload/remote",
			request,
		);
	}

	public static LinkCompetencyResource(request: ResourceRemoteLocationRequest) {
		return DataAdapter.Post<ResourceResponse>("resource/link/remote", request);
	}

	public static UploadFileResource(request: ResourceUploadFileRequest) {
		return DataAdapter.UploadForm<ResourceResponse>(
			"resource/upload/",
			request,
		);
	}

	public static UpdateCompetencyResource(request: ResourceUpdateRequest) {
		return DataAdapter.Post<ResourceResponse>("resource/update", request);
	}

	public static DeleteCompetencyResource(request: ResourceDeleteRequest) {
		return DataAdapter.Delete<ResourceDeleteResponse>(
			"resource/delete",
			request,
		);
	}

	public static DeleteProcedureComment(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`StepComments/${id}`,
		);
	}

	public static CreateNewCompetencySave(
		request: CreateCompetencyRequest,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"Competence/createnewcompetencysave",
			request,
		);
	}

	public static DeleteProcedureChecklist(
		request: DeleteProcedureChecklistRequest,
	) {
		return DataAdapter.Delete<boolean>(
			`checklist/${request.SchemeId}?procedureId=${request.ProcedureId}`,
		);
	}

	public static DeleteProcedureNode(request: DeleteProcedureNodeRequest) {
		return DataAdapter.Delete<DeleteProcedureNodeResponse>(
			`nodes/${request.SchemeId}`,
			request,
		);
	}

	public static GetDocumentsUsed(): Promise<DocumentFromProcedure[]> {
		return DataAdapter.Get<Array<DocumentFromProcedure>>({
			endpoint: "procedure/getdocumentsused",
		});
	}

	public static GetUppdatedProcedures(): Promise<UppdatedProcedureVersion[]> {
		return DataAdapter.Get<Array<UppdatedProcedureVersion>>({
			endpoint: "procedure/getuppdatedprocedures",
		});
	}

	public static UpdateNodeDocumentVersion(
		request: UpdateProcedureVersionRequest,
	) {
		return DataAdapter.Put<ResourceResponse>(
			`Nodes/${request.NodeSchemeId}/Version`,
			request,
		);
	}

	public static GetImportPlan(
		nodeId: number,
		checklistId: number,
	): Promise<DocumentImportPlan> {
		return DataAdapter.Get<DocumentImportPlan>({
			endpoint: `nodes/${nodeId}/checklists/${checklistId}/importPlan`,
		});
	}

	public static GetNewImportPlan(nodeId: number): Promise<DocumentImportPlan> {
		return DataAdapter.Get<DocumentImportPlan>({
			endpoint: `nodes/${nodeId}/importPlan`,
		});
	}

	public static CreateImportedChecklist(
		request: CreateImportedChecklistRequest,
	): Promise<UppdatedNodeResponse> {
		return DataAdapter.Post<UppdatedNodeResponse>(
			`checklist/imported`,
			request,
		);
	}

	public static UpdateImportedChecklist(
		request: UpdateImportedChecklistRequest,
	): Promise<UppdatedNodeResponse> {
		return DataAdapter.Put<UppdatedNodeResponse>(
			`checklist/${request.ChecklistId}/imported`,
			request,
		);
	}

	public static ReimportDocument(request: DocumentUpdateRequest | undefined) {
		return DataAdapter.PutWithNoResponseBody(
			`documents/${request?.FileId}`,
			request,
		);
	}

	public static UpdateSharepointDocuments() {
		return DataAdapter.PutWithNoResponseBody("Documents/Sharepoint", {});
	}

	public static GetTags(): Promise<Tag[]> {
		return DataAdapter.Get<Tag[]>({
			endpoint: "tags",
		});
	}

	public static UpdateTags(request: UpdateTagsRequest) {
		return DataAdapter.PutUploadForm("tags", request);
	}

	public static GetDrillFolders(): Promise<TrainingDrillFolder[]> {
		return DataAdapter.Get<TrainingDrillFolder[]>({
			endpoint: "TrainingDrillFolders",
		});
	}

	public static GetDrillFolder(id: number): Promise<TrainingDrillFolder> {
		return DataAdapter.Get<TrainingDrillFolder>({
			endpoint: `TrainingDrillFolders/${id}`,
		});
	}

	public static GetCourseFolders(): Promise<TrainingFolder[]> {
		return DataAdapter.Get<TrainingFolder[]>({
			endpoint: "TrainingCourseFolders",
		});
	}

	public static GetCourse(courseId: number): Promise<TrainingCourse> {
		return DataAdapter.Get<TrainingCourse>({
			endpoint: `TrainingCourses/${courseId}`,
		});
	}

	public static GetCourses(): Promise<TrainingCourseSummary[]> {
		return DataAdapter.Get<TrainingCourseSummary[]>({
			endpoint: "TrainingCourses",
		});
	}

	public static UpdateTrainingDrillFolder(
		request: TrainingDrillFolderRequest,
		folderId: number,
	): Promise<void> {
		return DataAdapter.PutWithNoResponseBody(
			`TrainingDrillFolders/${folderId}`,
			request,
		);
	}

	public static DeleteTrainingDrillFolder(folderId: number) {
		return DataAdapter.Delete(`TrainingDrillFolders/${folderId}`);
	}

	public static CreateTrainingFolder(
		request: TrainingFolderRequest,
	): Promise<TrainingFolder> {
		return DataAdapter.Post<TrainingFolder>(`TrainingCourseFolders`, request);
	}

	public static UpdateTrainingFolder(
		request: TrainingFolderRequest,
		folderId: number,
	): Promise<TrainingFolder> {
		return DataAdapter.Put<TrainingFolder>(
			`TrainingCourseFolders/${folderId}`,
			request,
		);
	}

	public static DeleteTrainingFolder(folderId: number) {
		return DataAdapter.Delete(`TrainingCourseFolders/${folderId}`);
	}

	public static GetTrainingLink(linkId: number) {
		return DataAdapter.Get<TrainingLink>({
			endpoint: `TrainingLinks/${linkId}`,
		});
	}

	public static CreateTrainingLink(
		request: TrainingLinkRequest,
	): Promise<TrainingLink> {
		return DataAdapter.Post<TrainingLink>(`TrainingLinks`, request);
	}

	public static UpdateTrainingLink(
		request: TrainingLinkRequest,
		linkId: number,
	): Promise<TrainingLink> {
		return DataAdapter.Put<TrainingLink>(`TrainingLinks/${linkId}`, request);
	}

	public static DeleteTrainingLink(linkId: number) {
		return DataAdapter.Delete(`TrainingLinks/${linkId}`);
	}

	public static CreateTrainingCourse(
		request: TrainingCourseRequest,
	): Promise<TrainingCourse> {
		return DataAdapter.UploadForm<TrainingCourse>(`TrainingCourses`, request);
	}

	public static UpdateTrainingCourse(
		request: TrainingCourseRequest,
		courseId: number,
	): Promise<TrainingCourse> {
		return DataAdapter.PutUploadForm<TrainingCourse>(
			`TrainingCourses/${courseId}`,
			request,
		);
	}

	public static DeleteTrainingCourse(courseId: number) {
		return DataAdapter.Delete(`TrainingCourses/${courseId}`);
	}

	public static DeleteTrainingCourseFile(courseId: number, fileId: number) {
		return DataAdapter.Delete(`TrainingCourses/${courseId}/files/${fileId}`);
	}

	public static GetDrill(drillId: number | undefined) {
		return DataAdapter.Get<TrainingDrill>({
			endpoint: `TrainingDrills/${drillId}`,
		});
	}

	public static GetDrills() {
		return DataAdapter.Get<DrillSummary[]>({
			endpoint: "TrainingDrills",
		});
	}

	public static CreateTrainingDrill(
		request: TrainingDrillRequest,
	): Promise<void> {
		return DataAdapter.PostWithNoResponseBody(`TrainingDrills`, request);
	}

	public static UpdateTrainingDrill(
		drillId: number,
		request: TrainingDrillRequest,
	) {
		return DataAdapter.PutWithNoResponseBody(
			`TrainingDrills/${drillId}`,
			request,
		);
	}

	public static DeleteTrainingDrill(drillId: number) {
		return DataAdapter.Delete(`TrainingDrills/${drillId}`);
	}

	public static DeleteTrainingDrillFile(drillId: number, fileId: number) {
		return DataAdapter.Delete(`TrainingDrills/${drillId}/files/${fileId}`);
	}

	public static GetActiveSessionsByDrillId(drillId: number) {
		return DataAdapter.Get<TrainingDrillSession[]>({
			endpoint: `TrainingDrills/${drillId}/Sessions?status=Active`,
		});
	}

	public static GetArchivedSessionsByDrillId(drillId: number) {
		return DataAdapter.Get<TrainingDrillSession[]>({
			endpoint: `TrainingDrills/${drillId}/Sessions?status=Archived`,
		});
	}

	public static GetDrillSession(sessionId: number) {
		return DataAdapter.Get<TrainingDrillSession>({
			endpoint: `TrainingDrillSessions/${sessionId}`,
		});
	}

	public static GetDrillSessions(queryOptions: TrainingSessionsQueryOptions) {
		const endpoint = buildEndpointWithQueryParameters(
			"TrainingDrillSessions",
			queryOptions,
		);

		return DataAdapter.Get<TrainingDrillSessionSummary[]>({
			endpoint,
		});
	}

	public static GetCourseSessions(queryOptions: TrainingSessionsQueryOptions) {
		const endpoint = buildEndpointWithQueryParameters(
			"TrainingSessions",
			queryOptions,
		);

		return DataAdapter.Get<TrainingSessionSummary[]>({
			endpoint,
		});
	}

	public static GetCourseSession(sessionId: number) {
		return DataAdapter.Get<TrainingSession>({
			endpoint: `TrainingSessions/${sessionId}`,
		});
	}

	public static GetActiveSessionsByCourseId(courseId: number) {
		return DataAdapter.Get<TrainingSessionSummary[]>({
			endpoint: `TrainingCourses/${courseId}/Sessions?status=Active`,
		});
	}

	public static GetArchivedSessionsByCourseId(courseId: number) {
		return DataAdapter.Get<TrainingSessionSummary[]>({
			endpoint: `TrainingCourses/${courseId}/Sessions?status=Archived`,
		});
	}

	public static GetDrillSessionsByKpiId(
		kpiId: number,
		queryOptions: TrainingKpiStatisticsQueryOptions,
	) {
		const endpoint = buildEndpointWithQueryParameters(
			`TrainingKpis/${kpiId}/sessions`,
			{ ...queryOptions, Status: "Completed" },
		);

		return DataAdapter.Get<TrainingDrillSessionSummaryWithKpiValue[]>({
			endpoint,
		});
	}

	public static CreateTrainingFile(
		request: TrainingFileRequest,
	): Promise<TrainingFile> {
		return DataAdapter.UploadForm<TrainingFile>(`TrainingFiles`, request);
	}

	public static CreateTrainingDrillSession(
		request: CreateTrainingDrillSessionRequest,
	): Promise<TrainingDrillSession> {
		return DataAdapter.Post<TrainingDrillSession>(
			`TrainingDrillSessions`,
			request,
		);
	}

	public static CreateTrainingSession(
		request: TrainingSessionRequest,
	): Promise<TrainingSession> {
		return DataAdapter.Post<TrainingSession>(`TrainingSessions`, request);
	}

	public static UpdateTrainingDrillSession(
		request: UpdateTrainingDrillSessionRequest,
	): Promise<TrainingDrillSession> {
		return DataAdapter.Put<TrainingDrillSession>(
			`TrainingDrillSessions/${request.Id}`,
			request,
		);
	}

	public static UpdateTrainingSession(
		sessionId: number,
		request: TrainingSessionRequest,
	): Promise<TrainingSession> {
		return DataAdapter.Put<TrainingSession>(
			`TrainingSessions/${sessionId}`,
			request,
		);
	}

	public static DeleteTrainingDrillSession(sessionId: number) {
		return DataAdapter.Delete(`TrainingDrillSessions/${sessionId}`);
	}

	public static DeleteTrainingSession(sessionId: number) {
		return DataAdapter.Delete(`TrainingSessions/${sessionId}`);
	}

	public static CompleteTrainingDrillSession(sessionId: number) {
		return DataAdapter.PatchWithNoPayload(
			`TrainingDrillSessions/${sessionId}/complete/`,
		);
	}

	public static CompleteTrainingSession(
		sessionId: number,
		request: CompleteTrainingSessionRequest,
	) {
		return DataAdapter.Patch(`TrainingSessions/${sessionId}/complete`, request);
	}

	public static ArchiveTrainingDrillSession(sessionId: number) {
		return DataAdapter.PatchWithNoPayload(
			`TrainingDrillSessions/${sessionId}/archive`,
		);
	}

	public static ArchiveTrainingSession(sessionId: number) {
		return DataAdapter.PatchWithNoPayload(
			`TrainingSessions/${sessionId}/archive`,
		);
	}

	public static DeleteTrainingSessionStudent(
		sessionId: number,
		studentId: number,
	) {
		return DataAdapter.Delete(
			`TrainingSessions/${sessionId}/students/${studentId}`,
		);
	}

	static GetStudentFailedReasons(): Promise<StudentFailedReason[]> {
		return DataAdapter.Get<StudentFailedReason[]>({
			endpoint: "StudentFailedReasons",
		});
	}

	static PostStudentFailedReason(
		request: StudentFailedReasonRequest,
	): Promise<StudentFailedReason> {
		return DataAdapter.Post<StudentFailedReason>(
			"StudentFailedReasons",
			request,
		);
	}

	static PutStudentFailedReason(
		id: number,
		request: StudentFailedReasonRequest,
	): Promise<StudentFailedReason> {
		return DataAdapter.Put<StudentFailedReason>(
			`StudentFailedReasons/${id}`,
			request,
		);
	}

	static DeleteStudentFailedReason(id: number) {
		return DataAdapter.Delete(`StudentFailedReasons/${id}`);
	}

	static GetKpis(): Promise<TrainingKpi[]> {
		return DataAdapter.Get<TrainingKpi[]>({
			endpoint: "TrainingKpis",
		});
	}

	static GetTrainingUsersWithMissedSessions(): Promise<TrainingUsersResponse> {
		return DataAdapter.Get<TrainingUsersResponse>({
			endpoint: "TrainingUsers",
		});
	}

	static PostKpi(request: TrainingKpiRequest): Promise<TrainingKpi> {
		return DataAdapter.Post<TrainingKpi>("TrainingKpis", request);
	}

	static PutKpi(id: number, request: TrainingKpiRequest): Promise<TrainingKpi> {
		return DataAdapter.Put<TrainingKpi>(`TrainingKpis/${id}`, request);
	}

	static DeleteKpi(id: number) {
		return DataAdapter.Delete(`TrainingKpis/${id}`);
	}

	static GetTrainingKpi(kpiId: number): Promise<TrainingKpi> {
		return DataAdapter.Get<TrainingKpi>({
			endpoint: `TrainingKpis/${kpiId}`,
		});
	}

	static GetTrainingKpiStatistics(
		queryOptions: TrainingKpiStatisticsQueryOptions,
	): Promise<TrainingKpiStatistic[]> {
		const endpoint = buildEndpointWithQueryParameters(
			"TrainingKpis/Statistics",
			{ ...queryOptions, Status: "Completed" },
		);

		return DataAdapter.Get<TrainingKpiStatistic[]>({
			endpoint,
		});
	}

	public static UpdateAzureAdGroups() {
		return DataAdapter.PutWithNoPayload(`User/AzureAd`);
	}

	public static UpdateAzureAdGlobalAdministrators() {
		return DataAdapter.PutWithNoPayload(`User/AzureAd/GlobalAdministrators`);
	}

	public static GetUserSummariesByCurrentCompany(): Promise<UserSummary[]> {
		return DataAdapter.Get<UserSummary[]>({
			endpoint: "user/FromCurrentOrganization",
		});
	}

	public static GetNodeVersionsComparison(nodeId: number, procedureId: number) {
		return DataAdapter.Get<NodeVersionComparison>({
			endpoint: `nodes/${nodeId}/Compare?procedureId=${procedureId}`,
		});
	}

	public static UpdateChecklist(
		request: Record<string, unknown>,
		checklistId: number,
		_procedureId: number,
	) {
		void _procedureId;
		return DataAdapter.Patch<unknown>(
			`Checklist/${checklistId}/UpdateManualChecklist`,
			request,
		);
	}

	public static UploadManualChecklistStepImage(request: {
		File: File;
		procedureId: number;
	}): Promise<FileUpload> {
		return DataAdapter.UploadForm<FileUpload>(
			"FileUpload/ManualChecklistImage",
			request,
		);
	}

	static GetManualMeasurementPoint(id: number) {
		return DataAdapter.Get<ManualMeasurementPointResponse>({
			endpoint: `MeasurementPoints/${id}`,
		});
	}

	static PostManualMeasurementPoint(request: ManualMeasurementPointRequest) {
		return DataAdapter.Post<ManualMeasurementPointResponse>(
			"MeasurementPoints",
			request,
		);
	}

	static PutManualMeasurementPoint(request: ManualMeasurementPointRequest) {
		return DataAdapter.Put<ManualMeasurementPointResponse>(
			`MeasurementPoints/${request.Id}`,
			request,
		);
	}

	static DeleteManualMeasurementPoint(id: number): Promise<void> {
		return DataAdapter.Delete(`MeasurementPoints/${id}`);
	}

	static GetManualMeasurementGroups() {
		return DataAdapter.Get<ManualMeasurementPointGroup[]>({
			endpoint: "MeasurementGroups",
		});
	}

	static GetManualMeasurementGroup(id: number) {
		return DataAdapter.Get<ManualMeasurementPointGroup>({
			endpoint: `MeasurementGroups/${id}`,
		});
	}

	static PostManualMeasurementGroup(
		request: CreateManualMeasurementGroupRequest,
	) {
		return DataAdapter.Post<ManualMeasurementPointGroup>(
			"MeasurementGroups",
			request,
		);
	}

	static PutManualMeasurementGroup(
		request: UpdateManualMeasurementGroupRequest,
	) {
		return DataAdapter.Put<ManualMeasurementPointGroup>(
			`MeasurementGroups/${request.Id}`,
			request,
		);
	}

	static DeleteManualMeasurementGroup(id: number) {
		return DataAdapter.Delete(`MeasurementGroups/${id}`);
	}

	static GetLiveSettings = (): Promise<LiveSettingsResponse> => {
		return DataAdapter.Get<LiveSettingsResponse>({
			endpoint: "SatosLive/Settings",
		});
	};

	static UpdateLiveSettings = (
		request: LiveSettingsRequest,
	): Promise<LiveSettingsResponse> => {
		return DataAdapter.Post<LiveSettingsResponse>(
			"SatosLive/Settings",
			request,
		);
	};

	static SaveLiveTagValue = (
		request: SavedLiveTagRequest,
	): Promise<SavedLiveTagResponse> => {
		return DataAdapter.Post<SavedLiveTagResponse>(
			"SatosLive/SaveLiveTagValue",
			request,
		);
	};

	// NodeDigram Base API
	static TogglePublishNodeDiagram = (
		request: TogglePublishNodeDiagramRequest,
	): Promise<number> => {
		return DataAdapter.Post<number>(`NodeDiagram/publish`, request);
	};

	// NodeDiagram Nodes API
	static addSingleDiagramNodeData = (
		node: DiagramNodeRequest,
	): Promise<DiagramNodeResponse> => {
		return DataAdapter.Post<DiagramNodeResponse>(`NodeDiagramNodes`, node);
	};

	static addExistingNodeDiagramNodes = (
		nodes: DiagramNodeRequest[],
	): Promise<DiagramNodeResponse[]> => {
		return DataAdapter.Post<DiagramNodeResponse[]>(
			`NodeDiagramNodes/existing`,
			nodes,
		);
	};

	static updateNodePosition = (
		node: UpdateNodePosition,
	): Promise<UpdateNodePosition> => {
		return DataAdapter.Patch<UpdateNodePosition>(`NodeDiagramNodes`, node);
	};

	static updateNodeParent = (node: UpdateNodeParent): Promise<number> => {
		return DataAdapter.Patch<number>(`NodeDiagramNodes/parent`, node);
	};

	static deleteNode = (
		request: DeleteNodeRequest,
	): Promise<DeleteNodeRequest> => {
		return DataAdapter.Delete<DeleteNodeRequest>(
			`NodeDiagramNodes/${request.NodeId}`,
			request,
		);
	};

	// NodeDiagram Groups API
	static addDiagramGroupData = (
		node: DiagramNodeRequest,
	): Promise<DiagramNodeResponse> => {
		return DataAdapter.Post<DiagramNodeResponse>(`NodeDiagramGroups`, node);
	};

	static updateGroup = (
		group: UpdateGroupRequest,
	): Promise<UpdateGroupRequest> => {
		return DataAdapter.Patch<UpdateGroupRequest>(`NodeDiagramGroups`, group);
	};

	static deleteGroup = (
		request: DeleteNodeRequest,
	): Promise<DeleteNodeRequest> => {
		return DataAdapter.Delete<DeleteNodeRequest>(
			`NodeDiagramGroups/${request.NodeId}`,
			request,
		);
	};

	// NodeDiagram Edges API
	static addDiagramEdge = (edge: UpdateEdgeRequest): Promise<void> => {
		return DataAdapter.Post(`NodeDiagramEdges`, edge);
	};

	static updateDiagramEdge = (
		edge: UpdateEdgeRequest,
	): Promise<UpdateEdgeRequest> => {
		return DataAdapter.Patch<UpdateEdgeRequest>(`NodeDiagramEdges`, edge);
	};

	static deleteDiagramEdge = (
		request: DeleteEdgeRequest,
	): Promise<DeleteEdgeRequest> => {
		return DataAdapter.Delete<DeleteEdgeRequest>(`NodeDiagramEdges`, request);
	};

	static deleteAllDiagramEdges = (ProcedureId: number): Promise<number> => {
		return DataAdapter.Delete<number>(`NodeDiagramEdges/${ProcedureId}`, {});
	};

	static fetchArchiveData = (procedureId: number | string) => {
		return DataAdapter.Get<ArchiveData>({
			endpoint: `archive/getGrid/${procedureId}`,
		});
	};

	// NodeDigram Base API
	static fetchNodeDiagramPublicity = (
		id: number,
	): Promise<NodeDiagramPublicityResponse> => {
		return DataAdapter.Get<NodeDiagramPublicityResponse>({
			endpoint: `NodeDiagram/${id}`,
		});
	};

	// NodeDiagram Nodes API
	static fetchNodeDiagramNodes = (
		id: number,
	): Promise<DiagramNodeResponse[]> => {
		return DataAdapter.Get<DiagramNodeResponse[]>({
			endpoint: `NodeDiagramNodes/${id}`,
		});
	};

	// NodeDiagram Groups API
	static fetchNodeDiagramGroups = (
		id: number,
	): Promise<DiagramNodeResponse[]> => {
		return DataAdapter.Get<DiagramNodeResponse[]>({
			endpoint: `NodeDiagramGroups/${id}`,
		});
	};

	// NodeDiagram Edges API
	static fetchNodeDiagramEdges = (id: number): Promise<Edge<any>[]> => {
		return DataAdapter.Get<Edge<any>[]>({
			endpoint: `NodeDiagramEdges/${id}`,
		});
	};

	static CreateChecklist = async (request: CreateChecklistRequest) => {
		return DataAdapter.Post<CreateChecklistResponse>(`checklist`, request);
	};

	static AddLiveTagToStep = async (request: AddLiveTagToStepRequest) => {
		return DataAdapter.Post(
			`checklistSteps/${request.StepId}/MeasurementTag`,
			request,
		);
	};

	static CreateChecklistStep = async (request: CreateChecklistStepRequest) => {
		return DataAdapter.Post(`checklistSteps`, request);
	};

	static UpdateChecklistStep = async (request: UpdateChecklistStepRequest) => {
		return DataAdapter.Patch(`checklistSteps/${request.StepId}`, request);
	};

	static DeleteChecklistStep = async (request: DeleteChecklistStepRequest) => {
		return DataAdapter.Delete(`checklistSteps/${request.StepId}`, request);
	};

	static RemoveLiveTagFromStep = async (
		request: RemoveLiveTagFromStepRequest,
	) => {
		return DataAdapter.Delete(
			`checklistSteps/${request.StepId}/LiveTag`,
			request,
		);
	};

	static RemoveManualMeasurementPointFromStep = async (
		request: RemoveManualMeasurementPointFromStepRequest,
	) => {
		return DataAdapter.Delete(
			`checklistSteps/${request.StepId}/ManualMeasurementPoint`,
			request,
		);
	};
}

export const buildEndpointWithQueryParameters = (
	endpoint: string,
	queryOptions: Record<string, unknown>,
) => {
	const activeQueryOptions = Object.entries(queryOptions).filter(
		([, value]) => value !== null && value !== undefined && value !== "",
	);
	const params = new URLSearchParams();
	activeQueryOptions.forEach(([key, value]) =>
		params.append(key, String(value)),
	);

	if (params.size > 0) {
		endpoint += `?${params.toString()}`;
	}

	return endpoint;
};
